import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import * as signalR from '@microsoft/signalr';
import { Music, Pause, Play, SkipForward, Check, X } from 'lucide-react';
import { RootState } from '../../configuration';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import {
  approveMusic,
  getMusicQueue,
  pauseMusic,
  rejectMusic,
  resumeMusic,
  setMusicVolume,
  skipMusic,
  MusicConfig,
  MusicItem,
  MusicQueue,
} from '../../api/payment/music-request.api';
import { formatMmSs, musicStatusLabel, queueItemLabel } from '../../utils/music-request';
import { createResponseGate } from '../../utils/response-gate';
import usePollWhile from '../../hooks/use-poll-while/use-poll-while';
import { routePaths } from '../../routes/routes-path';
import VolumeSlider from '../../components/volume-slider/volume-slider';
import { normalizeVolume } from '../../utils/overlay-volume';

/**
 * Bảng điều khiển "Phát nhạc theo yêu cầu" của streamer: duyệt / từ chối bài chờ duyệt, bỏ qua
 * bài đang phát, tạm dừng / tiếp tục, xem hàng chờ và lịch sử. Tự làm mới khi hub đẩy
 * `ReceiveMusicUpdate` (nối theo userId của chính mình) và tải lại dự phòng mỗi 15 giây.
 */

const RELOAD_FALLBACK_MS = 15000;
// Kéo thanh âm lượng: lưu sau khi tay dừng chừng này, không gọi máy chủ cho từng nấc.
const VOLUME_SAVE_DELAY_MS = 400;

const formatTime = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('vi-VN', {
    hour: '2-digit',
    minute: '2-digit',
    day: '2-digit',
    month: '2-digit',
  });
};

const thumbOf = (item: MusicItem) =>
  item.thumbnailUrl || `https://i.ytimg.com/vi/${item.videoId}/mqdefault.jpg`;

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

// Khai ngoài component trang để không bị dựng lại (mất trạng thái) mỗi lần trang render.
const MusicRow: React.FC<{
  item: MusicItem;
  showStatus?: boolean;
  large?: boolean;
  maxSeconds: number;
  /** Số thứ tự trong hàng chờ (kèm giờ dự kiến phát nếu máy chủ trả). */
  index?: number;
  children?: React.ReactNode;
}> = ({ item, showStatus, large, maxSeconds, index, children }) => (
  <li className="flex flex-col gap-2 border-b ui-divider py-3 last:border-b-0">
    <div className="flex gap-3">
      <a
        href={`https://www.youtube.com/watch?v=${item.videoId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="shrink-0"
        title="Mở bài trên YouTube"
      >
        <img
          src={thumbOf(item)}
          alt=""
          className={`${large ? 'h-[90px] w-[160px]' : 'h-[54px] w-[96px]'} rounded-lg bg-black object-cover`}
        />
      </a>
      <div className="min-w-0 flex-1">
        <a
          href={`https://www.youtube.com/watch?v=${item.videoId}`}
          target="_blank"
          rel="noopener noreferrer"
          className="line-clamp-2 font-semibold ui-heading hover:underline"
        >
          {item.title}
        </a>
        {item.author && <div className="truncate text-xs ui-muted">{item.author}</div>}
        {index !== undefined && (
          <div className="mt-1 text-xs font-semibold text-primary">
            {`Thứ ${item.position ?? index}`}
            {typeof item.etaSeconds === 'number' &&
              ` · ${item.etaSeconds <= 0 ? 'sắp tới lượt' : `dự kiến phát sau khoảng ${formatMmSs(item.etaSeconds)}`}`}
          </div>
        )}
        <div className="mt-1 text-sm ui-text">
          <span className="font-medium">{item.requesterName}</span>
          {item.isAnonymous && <span className="ui-muted"> (ẩn danh với người xem)</span>}
          <span className="ui-muted"> · </span>
          <span className="font-medium text-primary">{queueItemLabel(item, maxSeconds)}</span>
          <span className="ui-muted"> · {formatTime(item.createdAt)}</span>
        </div>
        {item.message && (
          <div className="mt-1 break-words text-sm italic ui-text">“{item.message}”</div>
        )}
        {showStatus && (
          <div className="mt-1 text-xs ui-muted">
            {musicStatusLabel(item.status)}
            {item.endedAt ? ` lúc ${formatTime(item.endedAt)}` : ''}
            {item.failReason ? `. ${item.failReason}` : ''}
          </div>
        )}
      </div>
    </div>
    {children && <div className="flex flex-wrap justify-end gap-2">{children}</div>}
  </li>
);

const Section: React.FC<{ title: string; count?: number; action?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  count,
  action,
  children,
}) => (
  <section className="ui-card rounded-xl p-4">
    <div className="mb-1 flex items-center justify-between gap-2">
      <h2 className="text-lg font-bold ui-heading">
        {title}
        {count !== undefined && <span className="ml-2 text-sm font-medium ui-muted">{count}</span>}
      </h2>
      {action}
    </div>
    {children}
  </section>
);

const Empty: React.FC<{ text: string }> = ({ text }) => <p className="py-3 text-sm ui-muted">{text}</p>;

const btnBase =
  'inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60';
const btnPrimary = `${btnBase} bg-blue-600 text-white hover:bg-blue-700`;
const btnOutline = `${btnBase} border border-[#C8CCD8] dark:border-slate-600 ui-text hover:bg-black/5 dark:hover:bg-white/5`;
const btnDanger = `${btnBase} border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/30`;

const MusicControlPage: React.FC = () => {
  const profile = useSelector((state: RootState) => state.features.auth.profile);
  const userId: string | undefined = profile?.userId;

  const [queue, setQueue] = useState<MusicQueue | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  // Chặn bấm hai lần ngay cả trước khi React kịp render trạng thái "đang xử lý".
  const inFlightRef = useRef<Set<string>>(new Set());
  const loadedOnceRef = useRef(false);
  // Tải lại do hub, do tải lại dự phòng và sau mỗi thao tác có thể chồng nhau: chỉ áp lần mới nhất.
  const gateRef = useRef(createResponseGate());

  // Âm lượng (khai ở đây vì load cần đọc): mỗi lần kéo tăng volumeEditSeqRef, lần lưu xong (thành công hay lỗi) đưa
  // volumeAckSeqRef lên bằng số thứ tự đã gửi. Hai số lệch nhau nghĩa là còn chỉnh tay chưa được máy chủ xác nhận.
  const volumeEditSeqRef = useRef(0);
  const volumeAckSeqRef = useRef(0);
  const localVolumeRef = useRef<number | null>(null);

  const volumeSettled = () => volumeEditSeqRef.current === volumeAckSeqRef.current;
  // Phản hồi của lời gọi gửi đi khi còn chỉnh âm lượng chưa xác nhận có thể mang số cũ hơn số streamer vừa kéo:
  // thay bằng số streamer đã kéo.
  const keepLocalVolume = (cfg: MusicConfig, settledAtIssue: boolean): MusicConfig => {
    const local = localVolumeRef.current;
    return !settledAtIssue && local !== null && cfg ? { ...cfg, volume: local } : cfg;
  };
  const keepLocalVolumeRef = useRef(keepLocalVolume);
  keepLocalVolumeRef.current = keepLocalVolume;

  const load = useCallback(async () => {
    const token = gateRef.current.issue();
    const settledAtIssue = volumeEditSeqRef.current === volumeAckSeqRef.current;
    try {
      const fetched = await getMusicQueue();
      if (!gateRef.current.acceptRead(token)) return;
      const q = fetched?.config ? { ...fetched, config: keepLocalVolumeRef.current(fetched.config, settledAtIssue) } : fetched;
      setQueue(q);
      setLoadError(false);
      loadedOnceRef.current = true;
    } catch {
      setLoadError(true);
      // Chỉ báo lần đầu, không báo lặp mỗi lần tải lại dự phòng.
      if (!loadedOnceRef.current) toast.error('Không tải được hàng chờ phát nhạc.');
    }
  }, []);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!userId) return undefined;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${PUBLIC_API_ENDPOINT}/payment-hubs?userid=${userId}`, {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();
    connection.on('ReceiveMusicUpdate', () => loadRef.current());
    connection.onreconnected(() => loadRef.current());
    connection.start().catch(() => {
      // Không nối được hub: vẫn còn tải lại dự phòng 15 giây.
    });
    return () => {
      connection.stop();
    };
  }, [userId]);

  usePollWhile(!!userId, () => loadRef.current(), RELOAD_FALLBACK_MS);

  // Âm lượng nhạc trên OBS: thanh trượt hiện số đang kéo (volumeDraft) cho tới khi máy chủ xác nhận lần chỉnh CUỐI
  // CÙNG. Phản hồi của lần lưu cũ hơn và lần tải lại gửi trước khi xác nhận đều không được kéo thanh về số cũ.
  // Lưu xong máy chủ đẩy hub, lớp phủ OBS đổi theo ngay.
  const [volumeDraft, setVolumeDraft] = useState<number | null>(null);
  const volumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingVolumeRef = useRef<number | null>(null);

  const saveVolume = useCallback((v: number, seq: number) => {
    setMusicVolume(v)
      .then((cfg) => {
        volumeAckSeqRef.current = Math.max(volumeAckSeqRef.current, seq);
        // Còn lần chỉnh mới hơn đang chờ gửi hoặc đang gửi: giữ nguyên số đang kéo.
        if (seq !== volumeEditSeqRef.current) return;
        // Chỉ lấy âm lượng từ phản hồi ghi, các trường khác vẫn đi qua tải lại có gate như cũ.
        setQueue((q) => (q ? { ...q, config: { ...q.config, volume: cfg?.volume ?? v } } : q));
        setVolumeDraft(null);
      })
      .catch((err) => {
        if (seq !== volumeEditSeqRef.current) return;
        volumeAckSeqRef.current = seq;
        localVolumeRef.current = null;
        setVolumeDraft(null);
        toast.error(errorMessage(err, 'Không đổi được âm lượng nhạc.'));
        loadRef.current();
      });
  }, []);

  const handleVolumeChange = (v: number) => {
    volumeEditSeqRef.current += 1;
    const seq = volumeEditSeqRef.current;
    localVolumeRef.current = v;
    setVolumeDraft(v);
    pendingVolumeRef.current = v;
    if (volumeTimerRef.current) clearTimeout(volumeTimerRef.current);
    volumeTimerRef.current = setTimeout(() => {
      volumeTimerRef.current = null;
      pendingVolumeRef.current = null;
      saveVolume(v, seq);
    }, VOLUME_SAVE_DELAY_MS);
  };

  // Rời trang ngay sau khi kéo: vẫn lưu số cuối cùng.
  useEffect(
    () => () => {
      if (volumeTimerRef.current) clearTimeout(volumeTimerRef.current);
      if (pendingVolumeRef.current !== null) setMusicVolume(pendingVolumeRef.current).catch(() => undefined);
    },
    []
  );

  const run = async <T,>(
    key: string,
    fn: () => Promise<T>,
    onOk: (result: T) => void,
    fallbackError: string
  ) => {
    if (inFlightRef.current.has(key)) return;
    inFlightRef.current.add(key);
    setBusy((b) => ({ ...b, [key]: true }));
    try {
      onOk(await fn());
    } catch (err) {
      toast.error(errorMessage(err, fallbackError));
    } finally {
      inFlightRef.current.delete(key);
      setBusy((b) => {
        const next = { ...b };
        delete next[key];
        return next;
      });
      loadRef.current();
    }
  };

  const handleApprove = (item: MusicItem) =>
    run(
      `approve:${item.id}`,
      () => approveMusic(item.id),
      (res) => {
        if (res.status === 'Failed') {
          toast.error(res.failReason || 'Không duyệt được bài này, người xem không bị trừ Ca.');
        } else {
          toast.success('Đã duyệt, bài vào hàng chờ');
        }
      },
      'Duyệt bài thất bại'
    );

  const handleReject = (item: MusicItem) => {
    if (!window.confirm(`Từ chối bài "${item.title}"? Người xem không bị trừ Ca.`)) return;
    run(
      `reject:${item.id}`,
      () => rejectMusic(item.id),
      () => toast.success('Đã từ chối bài'),
      'Từ chối bài thất bại'
    );
  };

  const handleSkip = () => {
    if (!window.confirm('Bỏ qua bài đang phát? Bài sẽ dừng ngay trên màn hình livestream.')) return;
    run('skip', () => skipMusic(), () => toast.success('Đã bỏ qua bài đang phát'), 'Bỏ qua bài thất bại');
  };

  const handleTogglePause = () => {
    const paused = !!queue?.config.isPaused;
    const settledAtIssue = volumeSettled();
    run(
      'pause',
      () => (paused ? resumeMusic() : pauseMusic()),
      (cfg) => {
        setQueue((q) => (q ? { ...q, config: keepLocalVolume(cfg, settledAtIssue) } : q));
        toast.success(paused ? 'Đã tiếp tục phát' : 'Đã tạm dừng phát');
      },
      paused ? 'Tiếp tục phát thất bại' : 'Tạm dừng thất bại'
    );
  };

  const config = queue?.config;
  const playing = queue?.playing ?? null;

  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4 px-4 py-6 md:px-6">
      <div className="flex items-center gap-3">
        <span className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-[#EAEFFA] text-[#0057FF] dark:bg-slate-700 dark:text-blue-300">
          <Music size={22} />
        </span>
        <div>
          <h1 className="text-[22px] font-bold ui-heading md:text-[26px]">Bảng điều khiển phát nhạc</h1>
          <p className="text-[14px] ui-muted">
            Duyệt bài người xem gửi, xem hàng chờ và điều khiển bài đang phát trên livestream.
          </p>
        </div>
      </div>

      {loadError && !queue && (
        <div className="flex items-center justify-between gap-2 rounded-lg border border-red-300 bg-red-50 p-3 dark:border-red-800 dark:bg-red-950/30">
          <span className="text-sm text-red-600 dark:text-red-400">Không tải được hàng chờ phát nhạc.</span>
          <button
            type="button"
            onClick={() => load()}
            className="shrink-0 rounded-lg border border-red-300 px-3 py-1 text-sm text-red-600 dark:border-red-700 dark:text-red-400"
          >
            Thử lại
          </button>
        </div>
      )}

      {!queue && !loadError && (
        <div className="ui-card flex items-center gap-3 rounded-xl p-4 text-sm ui-muted" role="status">
          <span className="size-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent" />
          Đang tải hàng chờ…
        </div>
      )}

      {config && !config.isEnabled && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
          Bạn đang tắt nhận yêu cầu phát nhạc, người xem không gửi được bài mới. Bài đã vào hàng chờ
          vẫn phát hết.{' '}
          <Link to={routePaths.donate} className="font-semibold underline">
            Mở cài đặt
          </Link>
        </div>
      )}

      {queue?.overlayActiveElsewhere && (
        <div
          role="alert"
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-300"
        >
          Đang có lớp phủ nhạc mở ở nơi khác. Chỉ một lớp phủ được phát, các lớp còn lại sẽ đứng
          im. Hãy đóng bớt tab hoặc cảnh OBS đang mở cùng đường dẫn nhạc.
        </div>
      )}

      {config?.isPaused && (
        <div className="rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-sky-800 dark:border-sky-700 dark:bg-sky-950/30 dark:text-sky-300">
          Đang tạm dừng: bài hiện tại dừng lại và bài kế tiếp chưa được phát cho tới khi bạn bấm Tiếp tục.
        </div>
      )}

      {queue && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Section title="Chờ duyệt" count={queue.pending.length}>
            {queue.pending.length === 0 ? (
              <Empty text="Chưa có bài nào chờ duyệt." />
            ) : (
              <ul>
                {queue.pending.map((item) => (
                  <MusicRow key={item.id} item={item} maxSeconds={config?.maxSeconds ?? 0}>
                    <button
                      type="button"
                      className={btnDanger}
                      disabled={!!busy[`reject:${item.id}`] || !!busy[`approve:${item.id}`]}
                      onClick={() => handleReject(item)}
                    >
                      <X size={14} />
                      {busy[`reject:${item.id}`] ? 'Đang từ chối…' : 'Từ chối'}
                    </button>
                    <button
                      type="button"
                      className={btnPrimary}
                      disabled={!!busy[`approve:${item.id}`] || !!busy[`reject:${item.id}`]}
                      onClick={() => handleApprove(item)}
                    >
                      <Check size={14} />
                      {busy[`approve:${item.id}`] ? 'Đang duyệt…' : 'Duyệt'}
                    </button>
                  </MusicRow>
                ))}
              </ul>
            )}
          </Section>

          <Section
            title="Đang phát"
            action={
              <button type="button" className={btnOutline} disabled={!!busy.pause} onClick={handleTogglePause}>
                {config?.isPaused ? <Play size={14} /> : <Pause size={14} />}
                {config?.isPaused ? 'Tiếp tục' : 'Tạm dừng'}
              </button>
            }
          >
            <div className="mb-2 mt-2">
              <VolumeSlider
                id="music-control-volume"
                label="Âm lượng nhạc trên OBS"
                value={volumeDraft ?? normalizeVolume(config?.volume)}
                onChange={handleVolumeChange}
                hint="OBS đổi theo sau khoảng một giây, bạn không cần tải lại."
              />
            </div>
            {playing ? (
              <ul>
                <MusicRow item={playing} large maxSeconds={config?.maxSeconds ?? 0}>
                  <button type="button" className={btnDanger} disabled={!!busy.skip} onClick={handleSkip}>
                    <SkipForward size={14} />
                    {busy.skip ? 'Đang bỏ qua…' : 'Bỏ qua'}
                  </button>
                </MusicRow>
              </ul>
            ) : (
              <Empty text="Chưa phát bài nào. Mở link overlay trong phần mềm stream để bắt đầu phát." />
            )}
          </Section>

          <Section title="Hàng chờ" count={queue.queued.length}>
            {queue.queued.length === 0 ? (
              <Empty text="Hàng chờ đang trống." />
            ) : (
              <ol>
                {queue.queued.map((item, i) => (
                  <MusicRow key={item.id} item={item} index={i + 1} maxSeconds={config?.maxSeconds ?? 0} />
                ))}
              </ol>
            )}
          </Section>

          <Section title="Lịch sử">
            {queue.history.length === 0 ? (
              <Empty text="Chưa có bài nào trong lịch sử." />
            ) : (
              <ul>
                {queue.history.map((item) => (
                  <MusicRow key={item.id} item={item} maxSeconds={config?.maxSeconds ?? 0} showStatus />
                ))}
              </ul>
            )}
          </Section>
        </div>
      )}
    </div>
  );
};

export default MusicControlPage;
